import { inject, Injectable, signal } from '@angular/core';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { ApiService, API_ENDPOINTS, CATEGORY } from '@core';
import { ApiResponse } from '@core/models/api.models';
import {
  DoorsWindowsSettingsResponse,
  DoorsWindowsSettingsSaveRequest,
  DrywallSettingsResponse,
  DrywallSettingsSaveRequest,
  DrywallSettingsSaveResponse,
  InteriorPaintSettingsResponse,
  InteriorPaintSettingsSaveRequest,
  InteriorPaintSettingsSaveResponse,
  PlumbingSettingsResponse,
  PlumbingSettingsSaveRequest,
  ScopeConfig,
  SettingsData,
  SettingsSaveResponse,
} from '@core/models/settings.models';

const DEFAULT_SETTINGS: SettingsData = {
  scopes: [
    {
      categoryId:   CATEGORY.FLOORING.numericId,
      categoryName: CATEGORY.FLOORING.name,
      parameters:   [],
      materials: [],
    },
    {
      categoryId:   CATEGORY.DRYWALL.numericId,
      categoryName: CATEGORY.DRYWALL.name,
      parameters: [
        { key: 'wall_height_ft', label: 'Default Wall Height', unit: 'ft', value: 9, min: 6, max: 30, step: 0.5 },
      ],
      materials: [],
    },
    {
      categoryId:   CATEGORY.DOORS_WINDOWS.numericId,
      categoryName: CATEGORY.DOORS_WINDOWS.name,
      parameters:   [],
      materials: [],
    },
    {
      categoryId:   CATEGORY.INTERIOR_PAINT.numericId,
      categoryName: CATEGORY.INTERIOR_PAINT.name,
      parameters: [
        { key: 'wall_height_ft', label: 'Default Wall Height', unit: 'ft', value: 8, min: 6, max: 30, step: 0.5 },
      ],
      materials: [],
    },
    {
      categoryId:   CATEGORY.FIXTURES.numericId,
      categoryName: 'Plumbing',
      parameters:   [],
      materials: [
        { id: 'main-shutoff-valve',          name: 'Main Shutoff Valve',              unit: 'each',       unitPrice: 0, active: true },
        { id: 'pressure-relief-valve',       name: 'Pressure Relief Valve',           unit: 'each',       unitPrice: 0, active: true },
        { id: 'urinal-flush-mechanism',      name: 'Urinal Flush Mechanism',          unit: 'each',       unitPrice: 0, active: true },
        { id: 'washing-machine-outlet-box',  name: 'Washing Machine Outlet Box',      unit: 'each',       unitPrice: 0, active: true },
        { id: 'escutcheons',                 name: 'Escutcheons',                     unit: 'each',       unitPrice: 3, active: true },
        { id: 'stop-valves',                 name: 'Stop Valves',                     unit: 'each',       unitPrice: 10, active: true },
        { id: 'supply-line',                 name: 'Supply Line',                     unit: 'each',       unitPrice: 10, active: true },
        { id: 'pex-mainline-pipe-3-4-inch',  name: '¾ inch Mainline Pex Tubing',      unit: 'roll',       unitPrice: 0, active: true },
        { id: 'pex-mainline-pipe-1-2-inch',  name: '½ inch Mainline Pex Tubing',      unit: 'roll',       unitPrice: 0, active: true },
        { id: 'pex-elbows-1-2-inch',         name: 'Box of ½" PEX Elbows',            unit: 'box',        unitPrice: 54.71, active: true },
        { id: 'pex-j-hooks-1-2-inch',        name: 'PEX J Hooks – ½"',               unit: 'box',        unitPrice: 13.98, active: true },
        { id: 'pex-bands-1-2-inch',          name: 'PEX Bands ½"',                   unit: 'box',        unitPrice: 30.65, active: true },
        { id: 'pex-bands-3-4-inch',          name: 'PEX Bands ¾"',                   unit: 'box',        unitPrice: 33.75, active: true },
        { id: 'pvc-drain-line-3-inch',       name: '3" PVC Drain Line',               unit: 'linear ft.', unitPrice: 33.86, active: true },
        { id: 'pvc-drain-line-2-inch',       name: '2" PVC Drain Line',               unit: 'linear ft.', unitPrice: 15.81, active: true },
        { id: 'pipe-couplers',               name: 'Pipe Couplers',                   unit: 'each',       unitPrice: 1.85, active: true },
        { id: 'cleanout-adapter',            name: 'Cleanout Adaptor with Plug',      unit: 'each',       unitPrice: 4.97, active: true },
        { id: 'standard-p-trap',             name: 'Standard P-Trap',                 unit: 'each',       unitPrice: 6, active: true },
        { id: 'toilet-flange',               name: 'Toilet Flange',                   unit: 'each',       unitPrice: 12, active: true },
        { id: 'wyes',                        name: 'Wyes',                            unit: 'each',       unitPrice: 9, active: true },
        { id: 'adapter-3x4',                 name: '3x4 Adapter',                     unit: 'each',       unitPrice: 6, active: true },
        { id: 'increaser-reducer-coupling',  name: 'Increaser/Reducer Coupling',      unit: 'each',       unitPrice: 7.36, active: true },
        { id: 'main-hose-bib',               name: 'Main Hose Bib (Outdoor Faucet)', unit: 'each',       unitPrice: 0, active: true },
      ],
      fixtures: [
        { id: 'toilet',               name: 'Toilet',               unit: 'each', unitPrice: 0, active: true },
        { id: 'bathroom-sink',        name: 'Bathroom Sink',        unit: 'each', unitPrice: 0, active: true },
        { id: 'sink',                 name: 'Sink',                 unit: 'each', unitPrice: 0, active: true },
        { id: 'bath-tub',             name: 'Bath Tub',             unit: 'each', unitPrice: 0, active: true },
        { id: 'shower',               name: 'Shower',               unit: 'each', unitPrice: 0, active: true },
        { id: 'refrigerator-box',     name: 'Refrigerator Box',     unit: 'each', unitPrice: 0, active: true },
        { id: 'washing-machine-box',  name: 'Washing Machine Box',  unit: 'each', unitPrice: 0, active: true },
        { id: 'urinal',               name: 'Urinal',               unit: 'each', unitPrice: 0, active: true },
        { id: 'dish-washer',          name: 'Dish Washer',          unit: 'each', unitPrice: 0, active: true },
        { id: 'water-heater',         name: 'Water Heater',         unit: 'each', unitPrice: 0, active: true },
        { id: 'water-cooler',         name: 'Water Cooler',         unit: 'each', unitPrice: 0, active: true },
      ],
    },
  ],
};

@Injectable()
export class SettingsService {
  private readonly api = inject(ApiService);
  private readonly _settings = signal<SettingsData>(structuredClone(DEFAULT_SETTINGS));

  readonly settings = this._settings.asReadonly();

  private settingsLoaded = false;

  load(force = false): Observable<ApiResponse<SettingsData>> {
    if (!force && this.settingsLoaded) {
      return of({ success: true, data: this._settings() } as ApiResponse<SettingsData>);
    }
    this.settingsLoaded = true;
    return this.api.get<SettingsData>(API_ENDPOINTS.project.adminSettings).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._settings.set(this.normalize(response.data));
        }
      }),
      catchError(err => {
        this.settingsLoaded = false;
        return throwError(() => err);
      }),
    );
  }

  save(data: SettingsData): Observable<ApiResponse<SettingsSaveResponse>> {
    const payload = this.normalize(data);
    return this.api.post<SettingsSaveResponse>(API_ENDPOINTS.project.adminSettings, payload).pipe(
      tap((response) => {
        if (response.success) {
          this._settings.set(payload);
        }
      }),
    );
  }

  getDrywallSettings(): Observable<ApiResponse<DrywallSettingsResponse>> {
    return this.api.get<DrywallSettingsResponse>(API_ENDPOINTS.adminSettings.drywall);
  }

  saveDrywallSettings(
    payload: DrywallSettingsSaveRequest,
  ): Observable<ApiResponse<DrywallSettingsSaveResponse>> {
    return this.api.post<DrywallSettingsSaveResponse>(API_ENDPOINTS.adminSettings.drywall, payload);
  }

  getDoorsWindowsSettings(): Observable<ApiResponse<DoorsWindowsSettingsResponse>> {
    return this.api.get<DoorsWindowsSettingsResponse>(API_ENDPOINTS.adminSettings.doorsWindows);
  }

  saveDoorsWindowsSettings(
    payload: DoorsWindowsSettingsSaveRequest,
  ): Observable<ApiResponse<DoorsWindowsSettingsResponse>> {
    return this.api.post<DoorsWindowsSettingsResponse>(
      API_ENDPOINTS.adminSettings.doorsWindows,
      payload,
    );
  }

  getInteriorPaintSettings(): Observable<ApiResponse<InteriorPaintSettingsResponse>> {
    return this.api.get<InteriorPaintSettingsResponse>(API_ENDPOINTS.adminSettings.interiorPaint);
  }

  getPlumbingSettings(): Observable<ApiResponse<PlumbingSettingsResponse>> {
    return this.api.get<PlumbingSettingsResponse>(API_ENDPOINTS.adminSettings.plumbing);
  }

  savePlumbingSettings(
    payload: PlumbingSettingsSaveRequest,
  ): Observable<ApiResponse<PlumbingSettingsResponse>> {
    return this.api.post<PlumbingSettingsResponse>(API_ENDPOINTS.adminSettings.plumbing, payload);
  }

  saveInteriorPaintSettings(
    payload: InteriorPaintSettingsSaveRequest,
  ): Observable<ApiResponse<InteriorPaintSettingsSaveResponse>> {
    return this.api.post<InteriorPaintSettingsSaveResponse>(
      API_ENDPOINTS.adminSettings.interiorPaint,
      payload,
    );
  }

  private normalize(data: Partial<SettingsData> | null | undefined): SettingsData {
    const scopes = structuredClone(data?.scopes ?? []);
    const mergedScopes = DEFAULT_SETTINGS.scopes.map((defaultScope) => {
      const scope = scopes.find((current) => Number(current.categoryId) === defaultScope.categoryId);
      return scope
        ? this.normalizeScope({ ...defaultScope, ...scope })
        : structuredClone(defaultScope);
    });

    for (const scope of scopes) {
      if (!mergedScopes.some((current) => current.categoryId === Number(scope.categoryId))) {
        mergedScopes.push(this.normalizeScope(scope));
      }
    }

    return { scopes: mergedScopes };
  }

  private normalizeScope(scope: ScopeConfig): ScopeConfig {
    return {
      ...scope,
      categoryId: Number(scope.categoryId),
      materials: scope.materials ?? [],
      fixtures: scope.fixtures ?? [],
      parameters: scope.parameters ?? [],
    };
  }
}
